import React, { useState, useEffect } from "react";
import { FaBitcoin, FaEthereum, FaCopy, FaSun } from "react-icons/fa";
import { SiLitecoin, SiMonero } from "react-icons/si";
import NotifyModal from "../components/NotifyModal"; // Import NotifyModal
import "../styles/Shop.css";

const Shop = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [email, setEmail] = useState('');
    const [processingPayment, setProcessingPayment] = useState(false);

    const donations = [
        { crypto: "Bitcoin (BTC)", address: "bc1q4ld9nzmgnn8hvq8jtvf58tlytnwdpden7x458r", icon: <FaBitcoin className="crypto-icon bitcoin" /> },
        { crypto: "Ethereum (ETH Network)", address: "0x5b843C4e9Bc24d4A0c58506fd7dE1B3cf6480893", icon: <FaEthereum className="crypto-icon ethereum" /> },
        { crypto: "USDT (ETH Network)", address: "0x5b843C4e9Bc24d4A0c58506fd7dE1B3cf6480893", icon: <FaEthereum className="crypto-icon tether" /> },
        { crypto: "Litecoin (LTC Network)", address: "LXn4xojed4D3WwLeHbRwtecWGnUXXMq7Mx", icon: <SiLitecoin className="crypto-icon litecoin" /> },
        { crypto: "Solana (SOL Network)", address: "4dDYPUf46LkKmqmyvK5yq9183QVi3FvuDMbzWzgqnEVD", icon: <FaSun className="crypto-icon solana" /> },
    ];

    const [notifyConfig, setNotifyConfig] = useState({
        show: false,
        type: '',
        message: ''
    });

    // Fetch products from Spring Boot backend
    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await fetch('http://localhost:8080/api/products');
            if (response.ok) {
                const data = await response.json();
                setProducts(data);
            } else {
                // Fallback to hardcoded products if backend is not available
                setProducts([
                    { id: 1, name: "30 Days", price: 9.99, duration: "30 Days" },
                    { id: 2, name: "6 Months", price: 49.99, duration: "6 Months" },
                    { id: 3, name: "Lifetime", price: 199.99, duration: "Lifetime" },
                ]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            // Fallback to hardcoded products
            setProducts([
                { id: 1, name: "30 Days", price: 9.99, duration: "30 Days" },
                { id: 2, name: "6 Months", price: 49.99, duration: "6 Months" },
                { id: 3, name: "Lifetime", price: 199.99, duration: "Lifetime" },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, type) => {
        setNotifyConfig({
            show: true,
            type,
            message
        });
    };

    const closeNotification = () => {
        setNotifyConfig({
            show: false,
            type: '',
            message: ''
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            showNotification("Address copied to clipboard!", "success");
        }).catch((err) => {
            console.error("Failed to copy: ", err);
            showNotification("Failed to copy address", "error");
        });
    };

    const handlePurchase = (product) => {
        setSelectedProduct(product);
        setEmailModalOpen(true);
    };

    const handleEmailSubmit = async () => {
        if (!email || !email.includes('@')) {
            showNotification("Please enter a valid email address", "error");
            return;
        }

        setProcessingPayment(true);

        try {
            const response = await fetch('http://localhost:8080/api/payment/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    productId: selectedProduct.id
                }),
            });

            if (response.ok) {
                const data = await response.json();
                showNotification("Payment created! Redirecting to payment page...", "success");

                // Redirect to Cryptomus payment page
                setTimeout(() => {
                    window.open(data.paymentUrl, '_blank');
                }, 1000);
            } else {
                const errorData = await response.json();
                showNotification(errorData.message || "Failed to create payment", "error");
            }
        } catch (error) {
            console.error('Error creating payment:', error);
            showNotification("Failed to create payment. Please try again.", "error");
        } finally {
            setProcessingPayment(false);
            setEmailModalOpen(false);
            setEmail('');
            setSelectedProduct(null);
        }
    };

    return (
        <>
            <NotifyModal
                type={notifyConfig.type}
                message={notifyConfig.message}
                isVisible={notifyConfig.show}
                onClose={closeNotification}
            />

            <section className="hero">
                <div className="hero-content">
                    <div className="hero-eyebrow">
                        <span className="status-indicator"></span>
                        Premium Access
                    </div>
                    <h1>
                        <span className="gradient-text">Choose Your License</span>
                    </h1>
                    <p className="hero-description">
                        Get full access to all ResourceDecrypt features with our flexible licensing options.
                    </p>
                </div>
            </section>

            <div className="shop-container">
                {loading ? (
                    <div className="loading">Loading products...</div>
                ) : (
                    <div className="license-grid">
                        {products.map((product) => (
                            <div key={product.id} className="license-card">
                                <h2>{product.duration || product.name}</h2>
                                <p className="price">${product.price}</p>
                                <button
                                    className="select-btn"
                                    onClick={() => handlePurchase(product)}
                                    disabled={processingPayment}
                                >
                                    {processingPayment ? 'Processing...' : 'Purchase Now'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Email Modal */}
                {emailModalOpen && (
                    <div className="modal-overlay" onClick={() => setEmailModalOpen(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h3>Enter Your Email</h3>
                            <p>We'll send your license to this email address after payment.</p>
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="email-input"
                            />
                            <div className="modal-buttons">
                                <button
                                    onClick={() => setEmailModalOpen(false)}
                                    className="cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEmailSubmit}
                                    className="submit-btn"
                                    disabled={processingPayment}
                                >
                                    {processingPayment ? 'Creating Payment...' : 'Continue to Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="payment-info">
                    <div className="accepted-payments">
                        <h3>We Accept</h3>
                        <div className="crypto-icons">
                            <FaBitcoin />
                            <FaEthereum />
                            <SiLitecoin />
                            <SiMonero />
                        </div>
                    </div>
                </div>

                <div className="donate-section">
                    <h3 className="donate-title">Support Us with a Donation</h3>
                    <p className="donate-description">
                        Thank you for considering supporting our project! You can donate using these cryptocurrency addresses:
                    </p>
                    <div className="crypto-list">
                        {donations.map((donation, index) => (
                            <div key={index} className="crypto-item">
                                <div className="crypto-header">
                                    {donation.icon}
                                    <span>{donation.crypto}</span>
                                </div>
                                <div className="crypto-address">
                                    <code>{donation.address}</code>
                                    <button
                                        className="copy-button"
                                        onClick={() => copyToClipboard(donation.address)}
                                    >
                                        <FaCopy />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Shop;